/**
 * zoom-dialog.js — Custom Lightbox
 * Top-right toolbar: zoom-in | zoom-out | play slideshow | fullscreen | close
 * Slide navigation with arrows, keyboard, swipe + thumbnail strip at bottom.
 */

(function () {
  'use strict';

  /* ─── Styles ─────────────────────────────────────────────────── */
  const STYLE = `
    .zd-overlay {
      position: fixed;
      inset: 0;
      z-index: 99999;
      background: rgba(10,10,12,0.96);
      display: flex;
      flex-direction: column;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.25s ease;
    }
    .zd-overlay.zd-open {
      opacity: 1;
      pointer-events: all;
    }

    /* ── Top bar ── */
    .zd-topbar {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 14px 20px 0;
      gap: 4px;
      position: relative;
      z-index: 20;
    }

    .zd-counter {
      margin-right: auto;
      color: rgba(255,255,255,0.45);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      letter-spacing: 0.1em;
      pointer-events: none;
    }

    /* Toolbar pill */
    .zd-toolbar {
      display: flex;
      align-items: center;
      gap: 2px;
      background: rgba(255,255,255,0.07);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 999px;
      padding: 4px 6px;
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .zd-tb-btn {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: transparent;
      border: none;
      color: rgba(255,255,255,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s, color 0.15s, transform 0.15s;
      flex-shrink: 0;
    }
    .zd-tb-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
    .zd-tb-btn:active { transform: scale(0.9); }
    .zd-tb-btn svg {
      width: 17px; height: 17px;
      fill: none; stroke: currentColor;
      stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round;
    }
    .zd-tb-btn.zd-play svg { stroke: none; fill: currentColor; }
    .zd-tb-btn.zd-play.playing svg { stroke: currentColor; fill: currentColor; }

    .zd-tb-sep {
      width: 1px; height: 18px;
      background: rgba(255,255,255,0.18);
      margin: 0 2px;
    }

    /* ── Stage ── */
    .zd-stage {
      flex: 1 1 auto;
      overflow: hidden;
      position: relative;
      display: flex;
      align-items: stretch;
    }

    .zd-track {
      display: flex;
      width: 100%;
      transition: transform 0.36s cubic-bezier(0.4,0,0.2,1);
      will-change: transform;
    }

    .zd-slide {
      flex: 0 0 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px 72px;
      box-sizing: border-box;
      overflow: hidden;
    }

    .zd-slide img {
      max-width: 100%; max-height: 100%;
      width: auto; height: auto;
      object-fit: contain;
      border-radius: 6px;
      transform-origin: center center;
      transform: scale(var(--zs,1)) translate(var(--ztx,0px),var(--zty,0px));
      transition: transform 0.2s ease;
      will-change: transform;
      user-select: none; -webkit-user-drag: none;
      cursor: zoom-in; display: block;
    }
    .zd-slide img.zoomed { cursor: grab; transition: none; }
    .zd-slide img.zoomed:active { cursor: grabbing; }

    /* ── Arrows ── */
    .zd-arrow {
      position: absolute; top: 50%; transform: translateY(-50%);
      z-index: 10;
      background: rgba(255,255,255,0.09);
      border: 1px solid rgba(255,255,255,0.16);
      color: #fff; width: 46px; height: 46px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; opacity: 0.8;
      transition: background 0.15s, opacity 0.15s;
      backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    }
    .zd-arrow:hover { background: rgba(255,255,255,0.2); opacity: 1; }
    .zd-arrow.zd-prev { left: 14px; }
    .zd-arrow.zd-next { right: 14px; }
    .zd-arrow.zd-hidden { opacity: 0; pointer-events: none; }
    .zd-arrow svg { width: 20px; height: 20px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

    /* ── Thumbs ── */
    .zd-thumbs {
      flex: 0 0 auto; width: 100%;
      padding: 8px 16px 14px; box-sizing: border-box;
      display: flex; justify-content: center;
      gap: 7px; overflow-x: auto; scrollbar-width: none;
    }
    .zd-thumbs::-webkit-scrollbar { display: none; }
    .zd-thumb {
      flex: 0 0 64px; width: 64px; height: 64px;
      border-radius: 4px; overflow: hidden; cursor: pointer;
      border: 2px solid transparent; opacity: 0.5;
      transition: opacity 0.18s, border-color 0.18s, transform 0.18s;
    }
    .zd-thumb:hover { opacity: 0.8; transform: scale(1.05); }
    .zd-thumb.active { border-color: rgba(255,255,255,0.75); opacity: 1; transform: scale(1.07); }
    .zd-thumb img { width: 100%; height: 100%; object-fit: cover; pointer-events: none; display: block; }

    @keyframes zd-in { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
    .zd-overlay.zd-open .zd-stage { animation: zd-in 0.28s ease forwards; }

    @media (max-width: 749px) {
      .zd-slide { padding: 12px 52px; }
      .zd-arrow { width: 36px; height: 36px; }
      .zd-arrow.zd-prev { left: 6px; }
      .zd-arrow.zd-next { right: 6px; }
      .zd-thumb { flex: 0 0 50px; width: 50px; height: 50px; }
    }
  `;

  const styleEl = document.createElement('style');
  styleEl.textContent = STYLE;
  document.head.appendChild(styleEl);

  /* ─── Icons ─────────────────────────────────────────────────── */
  const ic = d => `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">${d}</svg>`;
  const ICONS = {
    zoomIn:   ic('<circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="15.65" y2="15.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>'),
    zoomOut:  ic('<circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="15.65" y2="15.65"/><line x1="8" y1="11" x2="14" y2="11"/>'),
    play:     ic('<polygon points="5 3 19 12 5 21 5 3"/>'),
    pause:    ic('<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>'),
    fullscr:  ic('<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>'),
    exitFull: ic('<polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/>'),
    close:    ic('<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>'),
    prev:     ic('<polyline points="15 18 9 12 15 6"/>'),
    next:     ic('<polyline points="9 18 15 12 9 6"/>'),
  };

  /* ─── Custom element ─────────────────────────────────────────── */
  class ZoomDialog extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => this._init());
    }

    _init() {
      this._items     = [];
      this._current   = 0;
      this._zoom      = 1;
      this._tx        = 0;
      this._ty        = 0;
      this._playing   = false;
      this._playTimer = null;

      this._collectMedia();
      this._buildOverlay();
      this._bindTriggers();
      this._bindKeys();
    }

    _collectMedia() {
      const g = this.closest('media-gallery') || document;
      const hdUrls  = Array.from(g.querySelectorAll('.hd-zoom-url')).map(e => e.dataset.url).filter(Boolean);
      const thumbs  = Array.from(this.querySelectorAll('.dialog-thumbnails-list__thumbnail img')).map(i => i.src);
      const slides  = Array.from(g.querySelectorAll('slideshow-slide img'))
                        .filter(img => !img.closest('.dialog-thumbnails-list')).map(i => i.src);
      const n = Math.max(hdUrls.length, slides.length);
      this._items = Array.from({ length: n }, (_, i) => ({
        full:  hdUrls[i]  || slides[i] || '',
        thumb: thumbs[i]  || slides[i] || hdUrls[i] || '',
      }));
    }

    _buildOverlay() {
      const ov = document.createElement('div');
      ov.className = 'zd-overlay';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');

      /* Topbar */
      const topbar  = document.createElement('div');
      topbar.className = 'zd-topbar';

      const counter = document.createElement('div');
      counter.className = 'zd-counter';

      const toolbar = document.createElement('div');
      toolbar.className = 'zd-toolbar';

      const mkBtn = (key, label, fn, extraClass) => {
        const b = document.createElement('button');
        b.className = 'zd-tb-btn' + (extraClass ? ' ' + extraClass : '');
        b.setAttribute('aria-label', label);
        b.innerHTML = ICONS[key];
        b.addEventListener('click', fn);
        return b;
      };
      const sep = () => { const d = document.createElement('div'); d.className = 'zd-tb-sep'; return d; };

      const btnZoomIn  = mkBtn('zoomIn',  'Zoom in',          () => this._adjustZoom(0.5));
      const btnZoomOut = mkBtn('zoomOut', 'Zoom out',         () => this._adjustZoom(-0.5));
      const btnPlay    = mkBtn('play',    'Play slideshow',   () => this._togglePlay(), 'zd-play');
      const btnFull    = mkBtn('fullscr', 'Fullscreen',       () => this._toggleFullscreen());
      const btnClose   = mkBtn('close',   'Close',            () => this.close());

      this._btnPlay = btnPlay;
      this._btnFull = btnFull;

      toolbar.append(btnZoomIn, btnZoomOut, sep(), btnPlay, sep(), btnFull, sep(), btnClose);
      topbar.append(counter, toolbar);

      /* Stage */
      const stage = document.createElement('div');
      stage.className = 'zd-stage';

      const track = document.createElement('div');
      track.className = 'zd-track';

      const mkArrow = (dir, fn) => {
        const b = document.createElement('button');
        b.className = `zd-arrow zd-${dir}`;
        b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
        b.innerHTML = ICONS[dir];
        b.addEventListener('click', fn);
        return b;
      };
      const prevBtn = mkArrow('prev', () => this.prev());
      const nextBtn = mkArrow('next', () => this.next());
      stage.append(track, prevBtn, nextBtn);

      /* Thumbs */
      const thumbsBar = document.createElement('div');
      thumbsBar.className = 'zd-thumbs';

      /* Touch swipe */
      let _swipeX = 0;
      stage.addEventListener('touchstart', e => { _swipeX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend',   e => {
        const dx = e.changedTouches[0].clientX - _swipeX;
        if (Math.abs(dx) > 40) dx < 0 ? this.next() : this.prev();
      }, { passive: true });

      /* Backdrop click */
      ov.addEventListener('click', e => { if (e.target === ov) this.close(); });

      /* Fullscreen icon sync */
      document.addEventListener('fullscreenchange', () => {
        const inFS = !!document.fullscreenElement;
        this._btnFull.innerHTML = ICONS[inFS ? 'exitFull' : 'fullscr'];
        this._btnFull.setAttribute('aria-label', inFS ? 'Exit fullscreen' : 'Fullscreen');
      });

      ov.append(topbar, stage, thumbsBar);
      document.body.appendChild(ov);

      this._overlay   = ov;
      this._track     = track;
      this._counter   = counter;
      this._prevBtn   = prevBtn;
      this._nextBtn   = nextBtn;
      this._thumbsBar = thumbsBar;
    }

    _bindTriggers() {
      const id = this.id;
      if (!id) return;
      document.querySelectorAll(`[on\\:click^="#${id}/open/"]`).forEach(el => {
        el.addEventListener('click', e => {
          e.preventDefault();
          const parts = el.getAttribute('on:click').split('/');
          this.open(parseInt(parts[parts.length - 1], 10) || 0);
        });
      });
      const gallery = this.closest('media-gallery');
      if (gallery) {
        gallery.addEventListener('click', e => {
          const c = e.target.closest('.product-media-container--zoomable.product-media-container--image');
          if (!c) return;
          const all = Array.from(gallery.querySelectorAll(
            'slideshow-slide.product-media-container--image, li.product-media-container--image'
          ));
          const idx = all.indexOf(c);
          if (idx >= 0) this.open(idx);
        });
      }
    }

    _bindKeys() {
      document.addEventListener('keydown', e => {
        if (!this._overlay?.classList.contains('zd-open')) return;
        if (e.key === 'Escape')     this.close();
        if (e.key === 'ArrowLeft')  this.prev();
        if (e.key === 'ArrowRight') this.next();
        if (e.key === '+')          this._adjustZoom(0.5);
        if (e.key === '-')          this._adjustZoom(-0.5);
      });
    }

    /* ── Public ── */
    open(index = 0) {
      if (!this._overlay) return;
      this._collectMedia();
      this._rebuildSlides();
      this.goTo(index, false);
      this._overlay.classList.add('zd-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this._overlay?.classList.remove('zd-open');
      document.body.style.overflow = '';
      this._resetZoom(false);
      this._stopPlay();
      if (document.fullscreenElement) document.exitFullscreen?.();
    }

    goTo(index, animate = true) {
      const n = this._items.length;
      if (!n) return;
      this._current = Math.max(0, Math.min(index, n - 1));
      this._resetZoom(false);
      if (!animate) this._track.style.transition = 'none';
      this._track.style.transform = `translateX(-${this._current * 100}%)`;
      if (!animate) requestAnimationFrame(() => { this._track.style.transition = ''; });
      this._updateUI();
    }

    prev() { this.goTo(this._current - 1); }
    next() { this.goTo(this._current + 1); }

    /* ── Zoom ── */
    _adjustZoom(d) {
      this._zoom = Math.max(1, Math.min(5, this._zoom + d));
      if (this._zoom === 1) { this._tx = 0; this._ty = 0; }
      this._applyZoom();
    }
    _resetZoom(apply = true) {
      this._zoom = 1; this._tx = 0; this._ty = 0;
      if (apply) this._applyZoom();
    }
    _applyZoom() {
      const img = this._currentImg();
      if (!img) return;
      img.style.setProperty('--zs',  this._zoom);
      img.style.setProperty('--ztx', this._tx + 'px');
      img.style.setProperty('--zty', this._ty + 'px');
      img.classList.toggle('zoomed', this._zoom > 1);
    }

    /* ── Slideshow ── */
    _togglePlay() {
      this._playing ? this._stopPlay() : this._startPlay();
    }
    _startPlay() {
      this._playing = true;
      this._btnPlay.innerHTML = ICONS.pause;
      this._btnPlay.classList.add('playing');
      this._btnPlay.setAttribute('aria-label', 'Pause slideshow');
      this._playTimer = setInterval(() => {
        this.goTo((this._current + 1) % this._items.length);
      }, 3000);
    }
    _stopPlay() {
      this._playing = false;
      clearInterval(this._playTimer);
      this._playTimer = null;
      if (this._btnPlay) {
        this._btnPlay.innerHTML = ICONS.play;
        this._btnPlay.classList.remove('playing');
        this._btnPlay.setAttribute('aria-label', 'Play slideshow');
      }
    }

    /* ── Fullscreen ── */
    _toggleFullscreen() {
      document.fullscreenElement
        ? document.exitFullscreen?.()
        : this._overlay.requestFullscreen?.();
    }

    /* ── Image events ── */
    _attachImgEvents(img) {
      img.addEventListener('click', e => {
        if (this._zoom > 1) { this._resetZoom(); return; }
        const r  = img.getBoundingClientRect();
        const ox = ((e.clientX - r.left) / r.width  - 0.5) * r.width;
        const oy = ((e.clientY - r.top)  / r.height - 0.5) * r.height;
        this._zoom = 2.5;
        this._tx   = -ox;
        this._ty   = -oy;
        this._applyZoom();
      });
      img.addEventListener('wheel', e => {
        e.preventDefault();
        this._adjustZoom(e.deltaY > 0 ? -0.25 : 0.25);
      }, { passive: false });
      img.addEventListener('mousedown', e => {
        if (this._zoom <= 1) return;
        e.preventDefault();
        const sx = e.clientX - this._tx, sy = e.clientY - this._ty;
        const mv = ev => { this._tx = ev.clientX - sx; this._ty = ev.clientY - sy; this._applyZoom(); };
        const up = ()  => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
        window.addEventListener('mousemove', mv);
        window.addEventListener('mouseup',   up);
      });
    }

    _rebuildSlides() {
      if (!this._track) return;
      this._track.innerHTML     = '';
      this._thumbsBar.innerHTML = '';
      this._items.forEach((item, i) => {
        const slide = document.createElement('div');
        slide.className = 'zd-slide';
        const img = document.createElement('img');
        img.src = item.full; img.alt = ''; img.draggable = false;
        this._attachImgEvents(img);
        slide.appendChild(img);
        this._track.appendChild(slide);

        const thumb = document.createElement('div');
        thumb.className = 'zd-thumb';
        const tImg = document.createElement('img');
        tImg.src = item.thumb; tImg.alt = '';
        thumb.appendChild(tImg);
        thumb.addEventListener('click', () => this.goTo(i));
        this._thumbsBar.appendChild(thumb);
      });
    }

    _currentImg() {
      return this._track?.querySelectorAll('.zd-slide img')[this._current] || null;
    }

    _updateUI() {
      const n = this._items.length, i = this._current;
      if (this._counter) this._counter.textContent = `${i + 1} / ${n}`;
      this._prevBtn?.classList.toggle('zd-hidden', i === 0);
      this._nextBtn?.classList.toggle('zd-hidden', i === n - 1);
      this._thumbsBar?.querySelectorAll('.zd-thumb').forEach((t, idx) => {
        t.classList.toggle('active', idx === i);
      });
      this._thumbsBar?.querySelector('.zd-thumb.active')
        ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }

  customElements.define('zoom-dialog', ZoomDialog);
})();